import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import fetchWithAuth from '../utilities/fetchWithAuth'
import { useColorScheme } from '@mui/material/styles'

const SENSITIVE_SELECTION_KEYS = [
  'selectedAnalysisChain',
  'selectedDataItem',
  'selectedDevice',
  'selectedExperiment',
  'selectedFile',
  'selectedPlots',
  'selectedTriggerTimestamp',
  'selectedTriggerType',
]

const CurrentUserContext = createContext(null)

export const useUser = () => {
  const context = useContext(CurrentUserContext)
  if (context === undefined || context === null) {
    throw new Error('useUser must be used within a UserProvider')
  }
  return context
}

export const UserProvider = ({ children }) => {
  const { setMode } = useColorScheme()
  const setModeRef = useRef(setMode)
  useEffect(() => {
    setModeRef.current = setMode
  }, [setMode])

  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  const fetchUser = useCallback(async () => {
    setLoading(true)
    try {
      const { data, status } = await fetchWithAuth('users/me')
      if (status === 200) {
        setUser(data)
        setModeRef.current(data.settings.theme)
      } else {
        console.error('Failed to fetch user data:', status)
        setUser(null)
      }
    } catch (error) {
      console.error('Error fetching user data:', error)
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    SENSITIVE_SELECTION_KEYS.forEach((key) => localStorage.removeItem(key))
    setUser(null)
  }, [])

  useEffect(() => {
    const handler = () => logout()
    window.addEventListener('auth:failure', handler)
    return () => window.removeEventListener('auth:failure', handler)
  }, [logout])

  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      fetchUser()
    } else {
      setLoading(false)
    }
  }, [fetchUser])

  const value = {
    user,
    setUser,
    loading,
    fetchUser,
    logout,
  }

  if (!value) {
    throw new Error('UserProvider value is undefined')
  }

  return (
    <CurrentUserContext.Provider value={value}>
      {!loading ? children : null}
    </CurrentUserContext.Provider>
  )
}
